<template>
    <Field :name="name" v-slot="{ error }" ref="field">
        <div class="upload" :class="[classes, { 'upload--error':error }]">
            <Dropzone
                :id="id"
                :options="options"
                @vdropzone-sending="handleSending"
                @vdropzone-queue-complete="handleChanged"
                @vdropzone-removed-file="handleChanged"
                @vdropzone-error="handleError"
                ref="dropzone"
            >
            </Dropzone>
            <template v-if="multiple || isEmpty">
                <slot name="help" />
            </template>
            <template v-for="file in currentFiles">
                <input type="hidden" :name="inputName" :value="file.file_name">
            </template>
        </div>
    </Field>
</template>

<script>
    import Dropzone from 'vue2-dropzone';
    import Field from './Field';
    import { errorAlert } from "../util/alert";
    import { fileName } from "../util/file";

    export default {
        inject: {
            form: {
                default: null,
            }
        },
        components: {
            Dropzone,
            Field,
        },
        props: {
            id: String,
            files: Array,
            url: String,
            limit: {
                type: Number,
                default: 1
            },
            maxSize: Number,
            accept: String,
            csrfToken: {
                type: String,
                required: true,
            },
            name: {
                type: String,
                required: true,
            },
            imageOnly: Boolean,
        },
        data() {
            return {
                currentFiles: (this.files ?? []).filter(file => !!file),
            }
        },
        computed: {
            classes() {
                return {
                    'upload--unique': !this.multiple,
                }
            },
            options() {
                return {
                    url: this.url,
                    headers: {
                        'X-CSRF-TOKEN': this.csrfToken,
                    },
                    acceptedFiles: this.accept,
                    maxFilesize: this.maxSize,
                    dictDefaultMessage: this.message,
                    dictFileTooBig: `Fichier trop volumineux (max. ${this.maxSize}Mo)`,
                    dictCancelUpload: 'annuler le téléchargement',
                    dictCancelUploadConfirmation: 'Êtes-vous sûr d’annuler l’envoi du fichier ?',
                    dictRemoveFile: 'supprimer',
                    dictMaxFilesExceeded: `${this.limit} fichiers maximum`,
                    maxFiles: this.limit,
                    addRemoveLinks: true,
                }
            },
            multiple() {
                return this.limit > 1;
            },
            inputName() {
                return this.multiple ? `${this.name}[][file_name]` : `${this.name}[file_name]`;
            },
            message() {
                const baseMessage = '<a href="javascript:void(0)">Parcourir</a> ou glisser-déposer';
                if(this.imageOnly) {
                    return `${baseMessage} une image`;
                }
                if(this.limit === 1) {
                    return `${baseMessage} un fichier`;
                }
                return `${baseMessage} des fichiers`;
            },
            isEmpty() {
                return (this.currentFiles || []).length === 0;
            }
        },
        methods: {
            handleSending() {
                this.$emit('uploading', true);
                this.$refs.field.clear();
                this.form?.setLoading(this.name, true);
            },
            handleChanged() {
                this.$emit('uploading', false);
                this.currentFiles = (this.$refs.dropzone.getAcceptedFiles() ?? [])
                     .filter(file => file.status === 'success' || file.manuallyAdded)
                     .map(file => file.manuallyAdded ? file.data : ({
                         file_name: JSON.parse(file.xhr.responseText).name,
                     }));
                this.layout();
                this.form?.setLoading(this.name, false);
            },
            handleError(file, error, xhr) {
                let message = null;
                if(file.status === 'canceled') {
                    return;
                }
                if(xhr) {
                    message = xhr.status === 422
                        ? error.errors.file[0]
                        : 'Une erreur est survenue lors du téléchargement du fichier, veuillez réessayer';
                } else {
                    message = error;
                }
                errorAlert(this, message);
                this.$refs.dropzone.removeFile(file);
            },
            layout() {
                if(this.currentFiles.length < this.limit) {
                    this.$refs.dropzone?.enable();
                } else {
                    this.$refs.dropzone?.disable();
                }
            },
        },
        mounted() {
            this.currentFiles
                .forEach(file => {
                    this.$refs.dropzone.manuallyAddFile({
                        data: file,
                        name: fileName(file.file_name),
                        size: file.size,
                        accepted: true,
                    }, file.thumbnail ?? '');
                });
            this.layout();
        },
    }
</script>
